<template>
  <div class="expandable-toast-content">
    <div class="expandable-toast-header">
      <i :class="iconClass"></i>
      <div class="toast-text">
        <span class="toast-summary">{{ message.summary }}</span>
        <p v-if="fullDetail" class="toast-detail" :class="{ 'truncated': !expanded && shouldTruncate }">
          {{ displayedDetail }}
        </p>
      </div>
    </div>

    <div v-if="shouldTruncate || fullDetail" class="expandable-toast-actions">
      <button
        v-if="shouldTruncate"
        class="toast-action-btn"
        @click="toggleExpand"
      >
        <i :class="expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
        {{ expanded ? 'Show less' : 'Show more' }}
      </button>
      <button
        class="toast-action-btn"
        :title="copied ? 'Copied!' : 'Copy full message'"
        @click="copyContent"
      >
        <i :class="copied ? 'pi pi-check' : 'pi pi-copy'"></i>
        {{ copied ? 'Copied!' : 'Copy' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

const MAX_LENGTH = 150
const expanded = ref(false)
const copied = ref(false)

const severityConfig = {
  error: { icon: 'pi pi-times-circle', color: '#ef4444' },
  warn: { icon: 'pi pi-exclamation-triangle', color: '#f59e0b' },
  success: { icon: 'pi pi-check-circle', color: '#10b981' },
  info: { icon: 'pi pi-info-circle', color: '#3b82f6' }
}

const severity = computed(() => props.message.severity || 'info')

const iconClass = computed(() => {
  const config = severityConfig[severity.value] || severityConfig.info
  return `${config.icon} toast-icon toast-icon-${severity.value}`
})

const fullDetail = computed(() => {
  return props.message.data?.fullDetail || props.message.detail || ''
})

const shouldTruncate = computed(() => {
  return fullDetail.value.length > MAX_LENGTH
})

const displayedDetail = computed(() => {
  if (!shouldTruncate.value || expanded.value) {
    return fullDetail.value
  }
  return fullDetail.value.substring(0, MAX_LENGTH) + '...'
})

const toggleExpand = () => {
  expanded.value = !expanded.value
}

const copyContent = async () => {
  try {
    const label = severity.value === 'error' ? 'Error' :
                  severity.value === 'warn' ? 'Warning' :
                  severity.value === 'success' ? 'Success' : 'Info'
    const textToCopy = `${label}: ${props.message.summary}\n\nDetails:\n${fullDetail.value}`
    await navigator.clipboard.writeText(textToCopy)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<style scoped>
.expandable-toast-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem;
  max-width: 400px;
}

.expandable-toast-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.toast-icon {
  font-size: 1.5rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.toast-icon-error {
  color: #ef4444;
}

.toast-icon-warn {
  color: #f59e0b;
}

.toast-icon-success {
  color: #10b981;
}

.toast-icon-info {
  color: #3b82f6;
}

.toast-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.toast-summary {
  font-weight: 600;
  color: #e2e8f0;
  font-size: 0.95rem;
}

.toast-detail {
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
  word-break: break-word;
  white-space: pre-wrap;
  line-height: 1.4;
}

.toast-detail.truncated {
  overflow: hidden;
}

.expandable-toast-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
}

.toast-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background: rgba(148, 163, 184, 0.1);
  border: none;
  border-radius: 4px;
  color: #94a3b8;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.toast-action-btn:hover {
  background: rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
}

.toast-action-btn i {
  font-size: 0.75rem;
}
</style>
